import Page from 'components/layout/Page';
import { Whitelist, mixpassToken, MIXPASS_SALE_CONFIG } from 'components/Mixpass/mixpassConfig';
import MixpassDetails from 'components/Mixpass/MixpassDetails';
import MixpassWhitelist from 'components/Mixpass/MixpassWhitelist';
import { Box, Flex, Loading } from 'components/ui';
import { useWallet } from 'components/WalletContext';
import useMixpassSaleStatus from 'hooks/useMixpassSaleStatus';
import merge from 'lodash/merge';
import styled, { ThemeProvider } from 'styled-components';
import { theme } from 'utils/styles';
import ConnectedWalletGuard from 'components/ConnectedWalletGuard';
import { futuraChainId, wearablesOpenseaURL } from 'utils/config';
import VoxViewer from 'components/VoxViewer';
import PublicSale from 'components/Mixpass/PublicSale';
import { BigNumber } from '@ethersproject/bignumber';
import { Contracts, useContract } from 'hooks/useContract';
import { MixpassSale as MixpassSaleContract } from 'contracts';

const mixpassTheme = merge({}, theme, {
  colors: {
    ...theme.colors,
    highlight: theme.colors.oranges.light,
    background: theme.colors.cream,
    primary: theme.colors.skyBlue,
    secondary: theme.colors.creamsicle,
    error: '#ff0000',
    warning: '#ffe14a',
  },
});

const Anchor = styled.a`
  color: white;
  text-decoration: underline;
`;

const SoldOut = () => {
  return (
    <Box width="100%" position="relative">
      <VoxViewer model="futura-mixpass" width="100%" height="600px" />
      <Box
        position="absolute"
        top="120px"
        left={['0', '50%', '50%']}
        width={['100%', '600px', '600px']}
        marginLeft={['0', '-300px', '-300px']}
        background="rgba(0, 0, 0, 0.8)"
        p="4"
        borderRadius="8px"
      >
        <Box mb={2} fontSize="24px" fontWeight="bold">
          We have sold out!
        </Box>
        <Box>
          <a
            href={`${wearablesOpenseaURL}/${mixpassToken.address}/${mixpassToken.tokenId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'white' }}
          >
            Head to OpenSea
          </a>{' '}
          to buy Mixpass NFTs on the secondary market.
        </Box>
      </Box>
    </Box>
  );
};

const UserNotLoggedIn = () => {
  return (
    <Box p="3" margin="1em auto" minHeight="150px" width="100%" maxWidth="600px">
      <ConnectedWalletGuard />
    </Box>
  );
};

const PublicWhitelistSale = () => {
  const isSetup = Boolean(Whitelist.public);

  if (!isSetup) {
    return (
      <Box mt="5" py="2" px="3" mr="3" minHeight="150px" width={['100%', '100%', '50%']}>
        <Box fontSize={2} fontWeight="bold" mb="2" width="100%">
          Public Whitelist
        </Box>
        <Box mt={4}></Box>
      </Box>
    );
  }

  return <MixpassWhitelist whitelist={Whitelist.public} />;
};

const CommunityWhitelistSale = () => {
  return <MixpassWhitelist whitelist={Whitelist.community} />;
};

const MixpassSale = () => {
  const { isLoading, publicSaleEnabled, remainingSupply, paymentToken, price } = useMixpassSaleStatus();
  const { address, network, web3Provider } = useWallet();

  const contract = useContract(
    MIXPASS_SALE_CONFIG.contractAddress,
    Contracts.MixpassSale,
    futuraChainId
  );

  if (isLoading) {
    return <Loading />;
  }

  if (!address && !publicSaleEnabled) {
    return <UserNotLoggedIn />;
  }

  if (!web3Provider) {
    return <UserNotLoggedIn />;
  }

  // final check, web3 provider but no contract yet, show loading
  if (!contract) {
    return <Loading />;
  }

  if (Boolean(network) && network !== futuraChainId) {
    return <div>Configuration error, wrong network</div>
  }

  return (
    <Flex width="100%" flexDirection={['column', 'row']}>
      { publicSaleEnabled ? (
        <PublicSale 
          remainingSupply={remainingSupply}
          paymentToken={paymentToken}
          price={price as BigNumber} 
          contract={contract as MixpassSaleContract}
          />
      ) : (
        <>
          <CommunityWhitelistSale />
          <PublicWhitelistSale />
        </>
      )}
    </Flex>
  );
};

const MixpassMainContent = () => {
  const { remainingSupply } = useMixpassSaleStatus();

  if (remainingSupply && remainingSupply.eq(0)) {
    return <SoldOut />;
  }

  return (
    <Flex flexWrap={['wrap', 'wrap', 'nowrap']}>
      <Flex
        width={['100%', '100%', '40%']}
        order={[1, 1, 2]}
        px={[0, 0, 0, 4]}
        py={2}
        backgroundColor="black"
      >
        <MixpassDetails />
      </Flex>
      <Flex
        width={['100%', '100%', '60%']}
        px={['16px', '24px']}
        order={[2, 2, 1]}
        py={2}
        mt={[0, 0, 0]}
        column
        justifyCenter
      >
        <Box my={3} fontSize={20} fontWeight="bold">
          Futura Mixpass
        </Box>
        <Box mb={3}>
          The only way to mint a Futura Mix NFT will be through the purchase of a Futura Mixpass with Wrapped Eth (WETH) on the Polygon network.
          <br />
          Each Mixpass is redeemable for two Futura Mix NFTs.
        </Box>
        <Box background="black" p={3} borderRadius="8px">
          You must have sufficient ETH in your wallet on the Polygon network before you proceed. <br />
          We don’t prescribe a method to get ETH on the Polygon network. Here are two options to consider: <Anchor href="https://wallet.polygon.technology" target="_blank" rel="noopener noreferrer">Polygon Bridge</Anchor>{' '}
          and <Anchor href="https://bridge.umbria.network/bridge" target="_blank" rel="noopener noreferrer">Umbria Bridge</Anchor>. Click <Anchor href="https://eulerbeats.gitbook.io/eulerbeats-docs/futura#polygon-how-to" target="_blank" rel="noopener noreferrer">here</Anchor> for more information.
        </Box>
        <ConnectedWalletGuard networkId={futuraChainId}>
          <MixpassSale />
        </ConnectedWalletGuard>
      </Flex>
    </Flex>
  );
};

const FuturaMixpass = () => {
  return (
    <ThemeProvider theme={mixpassTheme}>
      <Page title="Futura Mixpass" padding="0" fixedFooter={false} backgroundColor="#333">
        <MixpassMainContent />
      </Page>
    </ThemeProvider>
  );
};

export default FuturaMixpass;
